/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Tab, Tabs } from "@mui/material";
import { PopoutContainer } from "components/common/PopoutContainer";
import { useTenantTranslation } from "hooks/formatters";
import { Dayjs } from "dayjs";
import { ReactNode, useMemo, useState } from "react";
import {
  LoadingWindowAssignment,
  EventCreate,
  HeatSummary,
  useGetHeatQualityIssuesQuery,
} from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";
import { ScrapEyeLayoutMapMini } from "components/scrapEyeLayout/ScrapEyeLayoutMapMini";
import {
  AreaColorMap,
  AreaMaterialsMap,
} from "components/scrapEyeLayout/scrapEyeLayoutCommon";
import { PoppableScaleWeightsPanel } from "./ScaleWeightsSidebarPanel";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { DayExceptionsPanel } from "./dayExceptions/DayExceptionsPanel";
import { HeatLoadingWindows } from "./components/HeatLoadingWindows";

export const MANUAL_REVIEW_SIDEBAR_WIDTH = 300;

export type ManualReviewSidebarTab = Readonly<{
  key: string;
  label: string;
  content: ReactNode;
}>;

type ManualReviewSidebarProps = Readonly<{
  nextDestination?: string | null;
  location: string;
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
  viewingWindowStart: Dayjs;
  viewingTime: Dayjs;
  massEvents: EventCreate[];
  heats: HeatSummary[];
  loadingWindows: LoadingWindowAssignment[];
  dayStart: Dayjs;
  dayEnd: Dayjs;
  extraTabs?: readonly ManualReviewSidebarTab[];
}>;

export const ManualReviewSidebar = ({
  nextDestination,
  location,
  areaColors,
  areaMaterials,
  viewingWindowStart,
  viewingTime,
  massEvents,
  heats,
  loadingWindows,
  dayStart,
  dayEnd,
  extraTabs,
}: ManualReviewSidebarProps) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { extra_data: extraData } = useScrapEyeMetadata();
  const hasScaleWeights = extraData?.includes("mass_data") ?? false;

  const hasLoadingWindows = loadingWindows.length > 0;

  const { data: qualityIssuesData } = useGetHeatQualityIssuesQuery({
    tenantName,
    start: dayStart.toISOString(),
    end: dayEnd.toISOString(),
  });
  const qualityIssues = qualityIssuesData?.issues ?? [];

  const tabs = useMemo(
    () => [
      ...(extraTabs ?? []).map(({ key, label }) => ({ key, label })),
      { key: "exceptions", label: t("exceptions") },
      ...(hasScaleWeights
        ? [{ key: "scaleWeights", label: t("scaleWeights") }]
        : []),
      ...(hasLoadingWindows ? [{ key: "heats", label: t("heats") }] : []),
      { key: "scrapLayout", label: t("scrapLayout") },
    ],
    [extraTabs, hasScaleWeights, hasLoadingWindows, t]
  );

  const [activeTab, setActiveTab] = useState(0);
  const activeTabKey = tabs[activeTab]?.key;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.paper",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(_, newValue: number) => {
          setActiveTab(newValue);
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 36,
          "& .MuiTab-root": {
            minHeight: 36,
            py: 0.5,
            fontSize: "0.7rem",
            fontWeight: 600,
            whiteSpace: "nowrap",
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </Tabs>
      <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {(extraTabs ?? []).map((tab) => (
          <Box
            key={tab.key}
            sx={{
              display: activeTabKey === tab.key ? "block" : "none",
              height: "100%",
              overflowY: "auto",
            }}
          >
            {tab.content}
          </Box>
        ))}
        {hasScaleWeights ? (
          <Box
            sx={{
              display: activeTabKey === "scaleWeights" ? "flex" : "none",
              height: "100%",
              alignItems: "flex-start",
              justifyContent: "center",
              p: 1,
              overflowY: "auto",
            }}
          >
            <PoppableScaleWeightsPanel
              videoStart={viewingWindowStart}
              massEvents={massEvents}
              errorBoundaryPanel="manual-review-mass-chart"
            />
          </Box>
        ) : null}
        <Box
          sx={{
            display: activeTabKey === "scrapLayout" ? "flex" : "none",
            height: "100%",
            alignItems: "flex-start",
            justifyContent: "center",
            p: 1,
            overflowY: "auto",
          }}
        >
          <PopoutContainer
            title={t("scrapLayout")}
            poppedOutMessage={t("layoutPoppedOut")}
          >
            <ScrapEyeLayoutMapMini
              highlightedArea={nextDestination}
              location={location}
              areaColors={areaColors}
              areaMaterials={areaMaterials}
            />
          </PopoutContainer>
        </Box>
        <Box
          sx={{
            display: activeTabKey === "exceptions" ? "block" : "none",
            height: "100%",
            overflowY: "auto",
          }}
        >
          <DayExceptionsPanel dayStart={dayStart} dayEnd={dayEnd} />
        </Box>
        {hasLoadingWindows ? (
          <Box
            sx={{
              display: activeTabKey === "heats" ? "block" : "none",
              height: "100%",
              overflowY: "auto",
              p: 1,
            }}
          >
            <HeatLoadingWindows
              loadingWindows={loadingWindows}
              heats={heats}
              viewingTime={viewingTime}
              qualityIssues={qualityIssues}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
